import { useMemo } from "react";
import Lottie from "react-lottie";

import StickyButton from "@/common/StickyButton";
import { ExperimentIds } from "@/constants/experiments";
import { FitnessGoals, goalTextById } from "@/constants/fitnessGoal";
import { QUESTIONS_IDS } from "@/constants/questionsIds";
import femaleGainMuscleGraphDataEn from "@/female/assets/lottie/gm_withWithoutZing_v1.json";
import femaleGainMuscleGraphDataEs from "@/female/assets/lottie/gm_withWithoutZing_v1-es.json";
import femaleLoseWeightGraphDataEn from "@/female/assets/lottie/lw_withWithoutZing_v1.json";
import femaleLoseWeightGraphDataEs from "@/female/assets/lottie/lw_withWithoutZing_v1-es.json";
import femaleYourEnergyGraphDataEn from "@/female/assets/lottie/yourEnergy_withWithoutZing_v1.json";
import femaleYourEnergyGraphDataEs from "@/female/assets/lottie/yourEnergy_withWithoutZing_v1-es.json";
import { useLocalization } from "@/hooks/useLocalization";
import i18n from "@/localization/i18next";
import gainMuscleGraphDataEn from "@/male/assets/lottie/gainMuscleGraphWithWithoutZing.json";
import gainMuscleGraphDataEs from "@/male/assets/lottie/gainMuscleGraphWithWithoutZing-es.json";
import loseWeightGraphDataEn from "@/male/assets/lottie/loseWeightGraphWithWithoutZing.json";
import loseWeightGraphDataEs from "@/male/assets/lottie/loseWeightGraphWithWithoutZing-es.json";
import femaleFitnessLevelStaticChart from "@/seniors/assets/charts/fitness-level.webp";
import femaleLoseWeightStaticChart from "@/seniors/assets/charts/lose-weight.webp";
import maleFitnessLevelStaticChart from "@/seniors/assets/charts/male-fitness-level.webp";
import maleLoseWeightStaticChart from "@/seniors/assets/charts/male-lose-weight.webp";
import maleMuscleMassStaticChart from "@/seniors/assets/charts/male-muscle-mass.webp";
import femaleMuscleMassStaticChart from "@/seniors/assets/charts/muscle-mass.webp";
import { HandleNext } from "@/types/common";
import { useExperiment } from "@/utils/ExperimentsProvider";
import { useGender } from "@/utils/GenderProvider";

import useAnswers from "../../utils/useAnswers";

const getChartsData = (isSpanish: boolean) => ({
    male: {
        [FitnessGoals.GainMuscle]: isSpanish ? gainMuscleGraphDataEs : gainMuscleGraphDataEn,
        [FitnessGoals.LoseWeight]: isSpanish ? loseWeightGraphDataEs : loseWeightGraphDataEn,
        [FitnessGoals.GetActive]: {},
        [FitnessGoals.BeHealthy]: isSpanish ? loseWeightGraphDataEs : loseWeightGraphDataEn,
        [FitnessGoals.GetInShape]: isSpanish ? gainMuscleGraphDataEs : gainMuscleGraphDataEn,
    },
    female: {
        [FitnessGoals.GainMuscle]: isSpanish
            ? femaleGainMuscleGraphDataEs
            : femaleGainMuscleGraphDataEn,
        [FitnessGoals.LoseWeight]: isSpanish
            ? femaleLoseWeightGraphDataEs
            : femaleLoseWeightGraphDataEn,
        [FitnessGoals.GetActive]: isSpanish
            ? femaleYourEnergyGraphDataEs
            : femaleYourEnergyGraphDataEn,
        [FitnessGoals.BeHealthy]: isSpanish
            ? femaleLoseWeightGraphDataEs
            : femaleLoseWeightGraphDataEn,
        [FitnessGoals.GetInShape]: isSpanish
            ? femaleGainMuscleGraphDataEs
            : femaleGainMuscleGraphDataEn,
    },
});

interface PromoHelpProps {
    handleNext: HandleNext;
    isActive: boolean;
}
const PromoHelp = ({ handleNext, isActive }: PromoHelpProps) => {
    const { isFemale } = useGender();
    const { isSpanish } = useLocalization();
    const { answers } = useAnswers();
    const primaryGoal = answers[QUESTIONS_IDS.PRIMARY_GOAL] as FitnessGoals;

    const lottieData = useMemo(() => {
        const charts = getChartsData(isSpanish);

        return isFemale ? charts.female[primaryGoal] : charts.male[primaryGoal];
    }, [isFemale, primaryGoal, isSpanish]);

    const description = i18n.t("zing_motivation_success", {
        goal: goalTextById[primaryGoal],
    });

    const { isTest: isSeniorsFunnelExp } = useExperiment({
        experimentId: ExperimentIds.SENIORS_FUNNEL,
    });

    const staticChartSrc = useMemo(
        () =>
            ({
                [FitnessGoals.GainMuscle]: isFemale
                    ? femaleMuscleMassStaticChart
                    : maleMuscleMassStaticChart,
                [FitnessGoals.LoseWeight]: isFemale
                    ? femaleLoseWeightStaticChart
                    : maleLoseWeightStaticChart,
                [FitnessGoals.GetActive]: isFemale
                    ? femaleFitnessLevelStaticChart
                    : maleFitnessLevelStaticChart,
            })[
                primaryGoal as
                    | FitnessGoals.GainMuscle
                    | FitnessGoals.LoseWeight
                    | FitnessGoals.GetActive
            ],
        [isFemale, primaryGoal]
    );

    return (
        <div className="grid h-full grow content-between gap-8">
            <div className="grid gap-6">
                <div className="grid gap-4">
                    <div className="text-zing-24-32 font-semibold text-white female:text-black">
                        {i18n.t("zing_motivation_track")}
                    </div>
                    <div className="text-white female:text-black">{description}</div>
                </div>
                <div className="-mx-6 md:mx-0">
                    {isSeniorsFunnelExp ? (
                        <img src={staticChartSrc} alt="progress chart" />
                    ) : (
                        <Lottie
                            options={{
                                animationData: lottieData,
                                loop: false,
                                autoplay: false,
                            }}
                            isPaused={!isActive}
                            isClickToPauseDisabled
                        />
                    )}
                </div>
            </div>
            <StickyButton className="bottom-0" onClick={() => handleNext()} />
        </div>
    );
};

export default PromoHelp;
